import { Injectable, inject } from '@angular/core';
import { Subscription, merge } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { WebviewService } from './webview.service';
import { WaypointService } from '../core/waypoint.service';
import { NavigationService } from '../core/navigation.service';
import { ConsoleLoggerService } from '../logging/console-logger.service';

/** One stop on the wire. `name` is null — never '' — when no address is known yet. */
interface HandoffWaypoint {
  lat: number;
  lng: number;
  name: string | null;
  csid: string | null;
}

/** Mirrors HandoffSnapshot.kt on the Android side. Keep the two in step. */
interface HandoffSnapshot {
  v: number;
  rev: number;
  origin: 'web' | 'car';
  mode: string;
  navigating: boolean;
  waypoints: HandoffWaypoint[];
  nextWaypointIndex: number;
  skippedChargingStations: string[];
}

const SCHEMA_VERSION = 2;

/**
 * Collapses the burst of emissions a single user action produces — moving a waypoint fires the
 * route list, the mode and the recalculation trigger in the same tick.
 */
const PUSH_DEBOUNCE_MS = 300;

/**
 * Hands this page's planning state to the car app.
 *
 * One direction only. The car app is not running while this page is, so there is nothing to
 * receive here — what the car leaves behind arrives the other way entirely, written straight into
 * localStorage before this app boots (see `car-handoff-storage.ts`), which is why nothing in here
 * has to guard against applying its own state back to itself.
 *
 * Pushed on every change rather than asked for at connect time, because by then this page is
 * gone: attaching to a head unit blanks the WebView, so the last push before that is the only
 * record of what the driver had planned.
 *
 * Everything is a no-op outside the native WebView.
 */
@Injectable({ providedIn: 'root' })
export class CarHandoffService {

  private readonly webviewService = inject(WebviewService);
  private readonly waypointService = inject(WaypointService);
  private readonly navigationService = inject(NavigationService);
  private readonly consoleLoggerService = inject(ConsoleLoggerService);

  private started = false;
  private subscriptions: Subscription[] = [];

  public init(): void {
    if (this.started || !this.webviewService.IsGPSTWebview()) return;
    this.started = true;

    this.subscriptions.push(
      merge(
        this.waypointService.ObservableRouteList,
        this.waypointService.ObservableSelectedDisplayMode,
        this.navigationService.ObservableChangeNavigation,
        // Skipping a charger changes none of the above — the stops and the mode are what they
        // were — so without this the skip would never reach the car and its replan would put the
        // charger straight back.
        this.navigationService.ObservableUserTriggeredReplan,
      ).pipe(
        debounceTime(PUSH_DEBOUNCE_MS),
      ).subscribe(() => this.push()),
    );
  }

  public destroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions = [];
    this.started = false;
  }

  /**
   * The last payload sent, without its revision. Compared before pushing so that emissions which
   * do not actually change the plan — a display name arriving late, a mode set to what it already
   * was — do not bump the revision the car uses to decide what is newer.
   */
  private lastSentContent: string | null = null;

  private push(): void {
    const content = this.buildContent();
    const contentJson = JSON.stringify(content);
    if (contentJson === this.lastSentContent) return;
    this.lastSentContent = contentJson;

    const snapshot: HandoffSnapshot = { ...content, rev: Date.now() };
    this.webviewService.PushWebPlanningState(JSON.stringify(snapshot));
    this.consoleLoggerService.log(
      '[handoff] pushed rev=' + snapshot.rev + ' mode=' + snapshot.mode +
      ' navigating=' + snapshot.navigating + ' waypoints=' + snapshot.waypoints.length +
      ' skipped=' + snapshot.skippedChargingStations.length
    );
  }

  /** Everything but the revision, so two snapshots can be compared for real change. */
  private buildContent(): Omit<HandoffSnapshot, 'rev'> {
    // Built from the route list rather than getWaypointsParams(), which is coord-only — the car
    // needs the addresses to label its stops, and re-geocoding them there would be wasteful.
    const waypoints: HandoffWaypoint[] = this.waypointService.getRouteList()
      .filter(el => el.wp != null && el.wp >= 0)
      .map(el => ({
        lat: el.lat,
        lng: el.lng,
        name: el.display_name && el.display_name.length > 0 ? el.display_name : null,
        csid: el.csid ?? null,
      }));

    return {
      v: SCHEMA_VERSION,
      origin: 'web',
      mode: this.waypointService.getSelectedDisplayMode(),
      navigating: this.navigationService.navigation === true,
      waypoints,
      nextWaypointIndex: 0,
      skippedChargingStations: this.skippedChargingStations(),
    };
  }

  /**
   * The chargers the driver has rejected on this trip.
   *
   * Read from storage rather than held here, because that is where the rest of the app keeps them
   * — NavigationService writes the list on each skip and the calculation service reads it back
   * when it builds a request. A parse failure is treated as nothing skipped: handing the car a
   * shorter list costs it a charger the driver did not want, which is recoverable, where handing
   * it a broken one would fail the whole push.
   */
  private skippedChargingStations(): string[] {
    let stored: string | null;
    try {
      stored = localStorage.getItem('skippedChargingStation');
    } catch {
      return [];
    }
    if (!stored || stored === 'null' || stored === 'undefined') return [];

    try {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed.map(id => String(id)) : [];
    } catch {
      return [];
    }
  }
}
